import { useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useDebug } from '../debug/DebugProvider'

import { useConnectivity } from '../contexts/ConnectivityContext'

const ENABLE_SCHOOL_REALTIME = true;

/**
 * Simplified Realtime Hook (Single Authenticated Channel)
 * Subscribes to all critical tables using one authenticated channel to ensure RLS compliance
 * and avoid connection limits.
 */
export function useSchoolRealtime({
  schoolId,
  teacherLessonIds = [],
  onUpdate,
  enabled = true
} = {}) {
  const { user } = useAuth()

  const subscriptionsRef = useRef([])
  const updateQueueRef = useRef([])
  const debounceTimerRef = useRef(null)
  const [retryTick, setRetryTick] = useState(0)
  const retryAttemptRef = useRef(0)
  const retryTimeoutRef = useRef(null)
  const [isConnected, setIsConnected] = useState(false)
  const { isOnline } = useConnectivity()
  const { isDebugMode } = useDebug()
  const onUpdateRef = useRef(onUpdate)
  const isMountedRef = useRef(true)

  // Update refs whenever deps change
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  // Debounced update handler
  const debouncedUpdate = useCallback((type, payload) => {
    updateQueueRef.current.push({ type, payload, timestamp: Date.now() })

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      const updates = [...updateQueueRef.current]
      updateQueueRef.current = []

      if (updates.length > 0) {
        if (!onUpdateRef.current) {
          console.error('[RT-ERROR] onUpdate callback is missing!')
          return
        }

        // Group updates by type
        const grouped = updates.reduce((acc, u) => {
          if (!acc[u.type]) acc[u.type] = []
          acc[u.type].push(u.payload)
          return acc
        }, {})

        // Dedupe within each type using stable keys
        const keyFor = (t, p) => {
          const r = p?.new || p?.old || p || {}
          if (t === 'attendance') return `${r.student_id || ''}|${r.lesson_id || ''}`
          if (t === 'diary') return `${r.lesson_id || ''}|${r.id || ''}`
          if (t === 'daily_log') return `${r.student_id || ''}|${r.date || ''}`
          if (t === 'lesson') return `${r.id || r.lesson_id || ''}`
          if (t === 'substitution') return `${r.id || ''}`
          if (t === 'bulletin') return `${r.id || ''}`
          if (t === 'staff_absence') return `${r.id || ''}`
          if (t === 'class_absence') return `${r.id || ''}`
          if (t === 'flex_resolution') return `${r.id || ''}`
          if (t === 'attendance_notes') return `${r.id || ''}`
          return `${r.id || r.lesson_id || r.student_id || ''}`
        }
        const groupedUpdates = Object.fromEntries(Object.entries(grouped).map(([t, list]) => {
          const map = new Map()
          for (const p of list) map.set(keyFor(t, p), p)
          return [t, Array.from(map.values()).map(payload => ({ type: t, payload }))]
        }))

        onUpdateRef.current(groupedUpdates)
      }
    }, 80)
  }, [])

  const debouncedUpdateRef = useRef(debouncedUpdate)
  const userRef = useRef(user)

  useEffect(() => {
    debouncedUpdateRef.current = debouncedUpdate
    userRef.current = user
  }, [debouncedUpdate, user])

  // Real-time subscription setup
  useEffect(() => {
    // 1. Validation
    if (!ENABLE_SCHOOL_REALTIME || !enabled || !schoolId || !user) {
      return;
    }

    let activeChannels = [];
    let hideTimer = null;
    let cancelled = false;
    // Re-entrancy guard for setupRealtime — without this, two rapid
    // calls (initial mount + visibility flip-back) both pass the
    // `activeChannels.length > 0` check during their await, then both
    // create channels.
    let setupInProgress = false;
    // Tracks whether we tore down due to hidden state; used to fire a
    // wake-event burst on re-subscribe so consumers refresh anything
    // they missed during the hidden window.
    let wokeUp = false;

    // Single source of truth for table → topic name mapping. Used to
    // register .on() bindings AND to drive the wake-event burst, so the
    // two paths can't drift.
    const BINDINGS = [
      { topic: 'daily_log',       table: 'student_daily_log' },
      { topic: 'attendance',      table: 'student_attendance_logs' },
      { topic: 'substitution',    table: 'substitutions' },
      { topic: 'lesson',          table: 'course_lessons' },
      { topic: 'bulletin',        table: 'bulletin_posts' },
      { topic: 'staff_absence',   table: 'staff_absences' },
      { topic: 'class_absence',   table: 'class_absences' },
      { topic: 'diary',           table: 'lesson_diary_entries' },
      { topic: 'flex_resolution', table: 'flex_overview_issue_resolutions' },
      // NOTE: Unterrichtsverteilung tables (verteilung_assignments,
      // staff_class_links, subject_grade_hours, subject_class_hours,
      // subjects, umfrage_form_config, umfrage_submissions,
      // profile_info_staff) lived here until 2026-05-26. They were moved
      // to a DEDICATED channel in features/scheduling/verteilungsplan/
      // hooks/useVerteilungsplanData.ts because this shared channel
      // started dropping verteilung events when carrying 17+ postgres_
      // changes bindings. Splitting keeps each channel single-purpose.
    ];

    // Hidden-tab grace before tearing down the WebSocket. Short enough
    // that long-backgrounded tabs stop holding realtime.subscription
    // rows on the server; long enough to ride out brief Cmd-Tab switches
    // without churning the channel.
    const HIDE_TEARDOWN_DELAY_MS = 60_000;

    const teardown = () => {
      activeChannels.forEach((ch) => {
        try { supabase.removeChannel(ch); } catch { /* no-op */ }
      });
      activeChannels = [];
      if (isMountedRef.current) setIsConnected(false);
    };

    const setupRealtime = () => {
      if (cancelled || setupInProgress || activeChannels.length > 0) return;
      setupInProgress = true;
      try {
        // JWT propagation is owned by AuthContext (#499) — it calls
        // `supabase.realtime.setAuth(session.access_token)` on every
        // SIGNED_IN / TOKEN_REFRESHED / INITIAL_SESSION, plus a
        // synchronous bootstrap from localStorage in
        // shared/lib/supabaseClient.ts. The legacy explicit
        // getSession+setAuth here was redundant — the effect already
        // gates on `user` being present, which is only set after auth
        // has resolved.

        // Single Channel Strategy (Proven Stability)
        // We use a single channel for all school events to avoid connection limits/timeouts.
        // Using the proven name 'att-realtime' which is known to work.
        const channelName = `att-realtime-${schoolId}`;
        const channel = supabase.channel(channelName);

        // All subscriptions live on a single channel for connection-limit
        // friendliness. The 80ms debounce in this hook + per-feature
        // throttles (e.g. FlexbandOverview's 1500ms `lastReloadRef`) keep
        // the refetch rate bounded even when many rows fire at once.
        // Topic names match what consumers pass to `subscribe(type, …)` —
        // see useSchoolRealtimeContext.
        for (const { topic, table } of BINDINGS) {
          channel.on('postgres_changes', {
            event: '*',
            schema: 'public',
            table,
            filter: `school_id=eq.${schoolId}`,
          }, (payload) => debouncedUpdateRef.current(topic, payload));
        }

        // J. Shared attendance-notes thread (secretariat ↔ teacher).
        channel.on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'student_attendance_notes',
          filter: `school_id=eq.${schoolId}`,
        }, (payload) => debouncedUpdateRef.current('attendance_notes', payload));

        channel.subscribe((status) => {
          // `cancelled` is the authoritative unmount signal — `isMountedRef`
          // is never flipped to false anywhere in this hook (pre-existing).
          if (cancelled) return;
          if (status === 'SUBSCRIBED') {
            setIsConnected(true);
            // After a re-subscribe following hidden→visible teardown,
            // emit one synthetic update per topic so consumers refetch
            // anything they missed while disconnected. Dedupe topics
            // (e.g. 'verteilung' is bound to multiple tables) so we don't
            // fire the same wake event N times.
            if (wokeUp) {
              wokeUp = false;
              const wokenTopics = new Set();
              for (const { topic } of BINDINGS) {
                if (wokenTopics.has(topic)) continue;
                wokenTopics.add(topic);
                debouncedUpdateRef.current(topic, { __wake__: true });
              }
            }
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn(`[RT] Connection issue on ${channelName}:`, status);
          }
        });

        activeChannels.push(channel);

      } catch (err) {
        console.error('[RT] Setup failed:', err);
      } finally {
        setupInProgress = false;
      }
    };

    // Visibility-aware lifecycle: hide for >60s → tear down to release
    // server-side realtime.subscription rows. Visible again → re-subscribe
    // and burst wake-events so consumers refresh any missed state.
    const onVisibility = () => {
      if (typeof document === 'undefined') return;
      const nowVisible = document.visibilityState !== 'hidden';
      if (!nowVisible) {
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          if (
            !cancelled
            && typeof document !== 'undefined'
            && document.visibilityState === 'hidden'
            && activeChannels.length > 0
          ) {
            teardown();
          }
        }, HIDE_TEARDOWN_DELAY_MS);
      } else {
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = null;
        }
        if (activeChannels.length === 0 && !cancelled) {
          wokeUp = true;
          setupRealtime();
        }
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    setupRealtime();

    // Evaluate the initial visibility state so a tab that mounts while
    // already hidden (background-tab open, app restored on next session)
    // schedules its teardown timer. Without this, the visibilitychange
    // listener only reacts to future transitions and the channel would
    // stay subscribed forever in an always-hidden tab.
    onVisibility();

    // 4. Cleanup
    return () => {
      cancelled = true;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      teardown();
    };
  }, [schoolId, user, enabled]);

  // Re-subscribe when connection is restored
  useEffect(() => {
    if (isOnline) {
      retryAttemptRef.current = 0
      setRetryTick((t) => t + 1)
    }
  }, [isOnline])

  // Manual test function
  const testRealtimeSubscription = useCallback(async () => {
    if (!isDebugMode || !schoolId) return false;
    try {
      const testDailyLog = {
        student_id: user.user_metadata?.profile_id || user.id,
        school_id: schoolId,
        date: new Date().toISOString().split('T')[0],
        presence_status: 'present',
        notes: `Frontend realtime test - ${new Date().toISOString()}`
      }
      const { data } = await supabase.from('student_daily_log').insert(testDailyLog).select()
      setTimeout(async () => {
        if (data?.[0]?.id) await supabase.from('student_daily_log').delete().eq('id', data[0].id)
      }, 5000)
      return true
    } catch (error) {
      return false
    }
  }, [schoolId, user, isDebugMode])

  return {
    isConnected,
    channelCount: isConnected ? 1 : 0,
    refresh: useCallback(() => {
      if (onUpdate) onUpdate({ refresh: [{ type: 'manual_refresh', timestamp: Date.now() }] })
    }, [onUpdate]),
    testRealtimeSubscription
  }
}

// Helper to enrich payload (kept for potential future use, though currently unused)
function enrichPayload(payload, type, tableName, schoolId) {
  const { eventType, new: newRecord, old: oldRecord, table } = payload || {}
  const record = newRecord || oldRecord || {}

  if (schoolId && record.school_id && record.school_id !== schoolId) {
    console.warn('[RT-FILTER-WARNING] School ID mismatch!', { record: record.school_id, current: schoolId })
  }

  return {
    ...payload,
    type,
    table: table || tableName,
    eventType: eventType || payload?.event || null,
    record: newRecord || oldRecord || null,
    old_record: oldRecord || null
  }
}
